import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef } from 'react';

const LandingVideoContext = createContext(null);

/**
 * Provider that creates a single video element and moves it between Hero and WhyFun
 * slots based on scroll position, so the same video appears in both sections
 * with only one network request.
 *
 * @param {object} props - Component props.
 * @param {string} [props.videoUrl] - URL of the landing video; no video element is created when omitted.
 * @param {string} [props.posterUrl] - URL of the poster image shown before the video plays.
 * @param {import("react").ReactNode} props.children - Landing content that renders the video slots.
 * @returns {import("react").JSX.Element} - The context provider wrapping `children`.
 */
export const LandingVideoProvider = ({ videoUrl, posterUrl, children }) => {
    const heroSlotRef = useRef(null);
    const whyFunSlotRef = useRef(null);
    const videoElRef = useRef(null);
    const observerRef = useRef(null);

    const moveVideoTo = useCallback((slotEl) => {
        if (!slotEl || !videoElRef.current) return;
        if (videoElRef.current.parentNode === slotEl) return;

        slotEl.appendChild(videoElRef.current);
    }, []);

    useLayoutEffect(() => {
        if (!videoUrl) return;

        if (typeof window !== 'undefined' && window.__PUPPETEER_PREBUILD_MODE__) {
            return;
        }

        const heroSlot = heroSlotRef.current;
        const whyFunSlot = whyFunSlotRef.current;

        if (!heroSlot) return;

        const video = document.createElement('video');
        video.src = videoUrl;
        video.poster = posterUrl || '';
        video.playsInline = true;
        video.muted = true;
        video.loop = true;
        video.autoplay = true;
        video.preload = 'metadata';
        video.className = 'phone__video-element';
        videoElRef.current = video;
        heroSlot.appendChild(video);

        const scheduleMove = () => {
            const whyFunSlot = whyFunSlotRef.current;

            if (!heroSlot) return;
            if (!whyFunSlot) {
                moveVideoTo(heroSlot);
                return;
            }

            const whyFunRect = whyFunSlot.getBoundingClientRect();
            const switchThreshold = window.innerHeight + 150;
            const whyFunInView = whyFunRect.top < switchThreshold;

            if (whyFunInView) {
                moveVideoTo(whyFunSlot);
            } else {
                moveVideoTo(heroSlot);
            }
        };

        scheduleMove();

        const hasIntersectionObserver = typeof IntersectionObserver !== 'undefined';
        let whyFunTimeoutId;

        if (hasIntersectionObserver) {
            observerRef.current = new IntersectionObserver(() => scheduleMove(), {
                threshold: [0, 0.25, 0.5, 0.75, 1],
                rootMargin: '-10% 0px 200px 0px',
            });

            if (heroSlot) observerRef.current.observe(heroSlot);
            if (whyFunSlot) observerRef.current.observe(whyFunSlot);

            whyFunTimeoutId = setTimeout(() => {
                if (whyFunSlotRef.current && !whyFunSlot)
                    observerRef.current?.observe(whyFunSlotRef.current);
            }, 0);
        }

        return () => {
            clearTimeout(whyFunTimeoutId);
            observerRef.current?.disconnect?.();
            observerRef.current = null;
            video.remove();
            videoElRef.current = null;
        };
    }, [videoUrl, posterUrl, moveVideoTo]);

    const contextValue = useMemo(
        () => ({ heroSlotRef, whyFunSlotRef }),
        [heroSlotRef, whyFunSlotRef]
    );

    return <LandingVideoContext value={contextValue}>{children}</LandingVideoContext>;
};

/**
 * Reads the shared landing video slot refs from the nearest `LandingVideoProvider`.
 *
 * @returns {{heroSlotRef: import("react").RefObject<HTMLElement | null>, whyFunSlotRef: import("react").RefObject<HTMLElement | null>} | null} - Refs of the Hero and WhyFun mount points the video moves between, or null outside the provider.
 */
export const useLandingVideo = () => useContext(LandingVideoContext);
